/**
 * Wallet Controller
 * Provides functions to extract and manage wallet data from the dashboard API response
 * @author Based on Dashboard implementation
 */

import { getHomePageInfos, getAllEWalletsInfo, getAllEWalletsAmount } from '../utils/apiClient';
import AppConstants from '../utils/appConstants';

/** Currency.TYPE on server matches Flutter CurrencyType: TRADING=3, LOCAL=4 — excluded from cash-in/out wallet pickers. */
const CURRENCY_TYPE_TRADING = 3;
const CURRENCY_TYPE_LOCAL = 4;

class WalletController {
  /**
   * Merge wallet arrays from all trading accounts (matches updateEWalletsInfo).
   * Dedupes by wallet ID so a consolidated cache row does not list the same wallet twice.
   */
  static collectWalletsFromAccounts(accounts) {
    if (!Array.isArray(accounts) || accounts.length === 0) return [];
    const seen = new Set();
    const out = [];
    for (const account of accounts) {
      if (!account?.wallet || !Array.isArray(account.wallet)) continue;
      for (const w of account.wallet) {
        const id = w?.ID != null ? String(w.ID) : w?.id != null ? String(w.id) : null;
        if (id) {
          if (seen.has(id)) continue;
          seen.add(id);
        }
        out.push(w);
      }
    }
    return out;
  }

  /**
   * Wallets eligible for cash-in / cash-out (matches Flutter: not TRADING or LOCAL currency types).
   * When TYPE is omitted (e.g. partial API payload), the wallet is kept.
   */
  static filterCashOperationWallets(wallets) {
    if (!Array.isArray(wallets)) return [];
    return wallets.filter((w) => {
      const t = w?.currencies?.TYPE ?? w?.currencies?.type ?? w?.CURRENCY_TYPE;
      if (t === undefined || t === null || t === '') return true;
      const n = Number(t);
      if (Number.isNaN(n)) return true;
      return n !== CURRENCY_TYPE_TRADING && n !== CURRENCY_TYPE_LOCAL;
    });
  }

  /** True when get-all-ewallets-info returned a wallet row without usable currency fields */
  static walletNeedsCurrencyMerge(w) {
    const c = w?.currencies;
    if (!c || typeof c !== 'object') return true;
    const code = c.CURRENCY_CODE || c.currency_code || c.currencyCode;
    const sym = c.CURRENCY_SYMBOL || c.currency_symbol || c.currencySymbol;
    return !code && !sym;
  }

  /**
   * Copy `currencies` from cached get-home-page-infos userData when the e-wallet endpoint omits them
   * (Laravel eager-load select without Currency.ID historically broke the relation).
   */
  static mergeCurrenciesFromUserData(wallets, userData) {
    if (!Array.isArray(wallets) || wallets.length === 0) return;
    const accounts = userData?.account;
    if (!Array.isArray(accounts)) return;
    const idToCurrencies = new Map();
    for (const acc of accounts) {
      if (!acc?.wallet || !Array.isArray(acc.wallet)) continue;
      for (const w of acc.wallet) {
        const id = w?.ID != null ? String(w.ID) : w?.id != null ? String(w.id) : null;
        const cur = w?.currencies;
        if (!id || !cur || typeof cur !== 'object') continue;
        const code = cur.CURRENCY_CODE || cur.currency_code || cur.currencyCode;
        const sym = cur.CURRENCY_SYMBOL || cur.currency_symbol || cur.currencySymbol;
        if (code || sym) idToCurrencies.set(id, cur);
      }
    }
    for (const w of wallets) {
      const id = w?.ID != null ? String(w.ID) : w?.id != null ? String(w.id) : null;
      if (!id || !this.walletNeedsCurrencyMerge(w)) continue;
      const fallback = idToCurrencies.get(id);
      if (fallback) w.currencies = fallback;
    }
  }

  /**
   * Load e-wallets via get-all-ewallets-info + get-all-ewallets-amount (same source as API WalletController / Flutter EWalletController).
   * Does not overwrite sessionStorage — safe to use on cash-in/out without mutating dashboard cache shape.
   */
  static async fetchEWalletsWithAmounts() {
    const infoResponse = await getAllEWalletsInfo();
    const infoData = infoResponse?.decryptedData || infoResponse?.data;
    if (!infoData || infoData.CODE !== AppConstants.responseOk) {
      const msg = infoData?.MESSAGE || `API Error (Code: ${infoData?.CODE || 'Unknown'})`;
      throw new Error(msg);
    }
    const accounts = Array.isArray(infoData.DATA) ? infoData.DATA : [];
    const wallets = this.collectWalletsFromAccounts(accounts);
    try {
      const stored = sessionStorage.getItem('userData');
      if (stored) {
        this.mergeCurrenciesFromUserData(wallets, JSON.parse(stored));
      }
    } catch (_) {
      /* ignore cache parse errors */
    }
    if (wallets.length === 0) {
      return { wallets: [], accounts, fullResponse: infoData };
    }
    const amountResult = await this.updateEWalletsAmount(wallets);
    const withBalances = amountResult.wallets || wallets;
    return {
      wallets: this.filterCashOperationWallets(withBalances),
      accounts,
      notificationCount: amountResult.notificationCount,
      chatNotificationCount: amountResult.chatNotificationCount,
      notifications: amountResult.notifications,
      fullResponse: infoData,
    };
  }

  /**
   * Get wallet data from the dashboard API response
   * @returns {Promise<Object>} Object containing wallets array and full response data
   * @throws {Error} If API call fails or response is invalid
   */
  static async getWalletsFromDashboard() {
    try {
      // Call the dashboard API
      const response = await getHomePageInfos();
      const data = response?.decryptedData || response?.data;
      // Check if response is successful
      if (!data || data.CODE !== AppConstants.responseOk) {
        const errorMessage = data?.MESSAGE || `API Error (Code: ${data?.CODE || 'Unknown'})`;
        throw new Error(errorMessage);
      }
      
      let wallets = [];
      if (data.DATA && data.DATA.account && Array.isArray(data.DATA.account) && data.DATA.account.length > 0) {
        wallets = this.collectWalletsFromAccounts(data.DATA.account);
      }
      
      return {
        wallets: wallets,
        userData: data.DATA || null,
        notifications: data.NOTIFICATIONS || null,
        pendingAgreement: data.PENDING_AGREEMENT || null,
        fullResponse: data
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get wallets from cached sessionStorage data (if available)
   * @returns {Object|null} Object containing wallets array and user data, or null if not cached
   */
  static getWalletsFromCache() {
    try {
      const storedUserData = sessionStorage.getItem('userData');
      const storedTimestamp = sessionStorage.getItem('userDataTimestamp');
      
      // Check if cached data exists and is less than 5 minutes old
      if (storedUserData && storedTimestamp) {
        const age = Date.now() - parseInt(storedTimestamp, 10);
        if (age < 5 * 60 * 1000) { // 5 minutes
          const userData = JSON.parse(storedUserData);
          
          let wallets = [];
          if (userData.account && Array.isArray(userData.account) && userData.account.length > 0) {
            wallets = this.collectWalletsFromAccounts(userData.account);
          }
          
          return {
            wallets: wallets,
            userData: userData,
            fromCache: true
          };
        }
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get wallets - tries cache first, then API if cache is unavailable or stale
   * @param {boolean} forceRefresh - If true, bypasses cache and fetches from API
   * @returns {Promise<Object>} Object containing wallets array and user data
   */
  static async getWallets(forceRefresh = false) {
    // Try cache first if not forcing refresh
    if (!forceRefresh) {
      const cachedData = this.getWalletsFromCache();
      if (cachedData) {
        return cachedData;
      }
    }
    
    // Fetch from API if cache is unavailable or refresh is forced
    return await this.getWalletsFromDashboard();
  }

  /**
   * Update e-wallets info (reload all information)
   * Similar to Flutter's updateEWalletsInfo()
   * @returns {Promise<Object>} Object containing updated wallets array and user data
   * @throws {Error} If API call fails or response is invalid
   */
  static async updateEWalletsInfo() {
    try {
      // Call the get-all-ewallets-info API
      const response = await getAllEWalletsInfo();
      const data = response?.decryptedData || response?.data;
      
      // Validate response
      if (!data || data.CODE !== AppConstants.responseOk) {
        const errorMessage = data?.MESSAGE || `API Error (Code: ${data?.CODE || 'Unknown'})`;
        throw new Error(errorMessage);
      }
      
      // Parse accounts from DATA (similar to Flutter: user?.accounts?.add(AccountModel.fromJson(element)))
      let accounts = [];
      if (data.DATA && Array.isArray(data.DATA)) {
        accounts = data.DATA;
      }
      
      // Extract wallets from all accounts
      let wallets = [];
      accounts.forEach((account) => {
        if (account.wallet && Array.isArray(account.wallet)) {
          wallets = wallets.concat(account.wallet);
        }
      });
      
      // Update sessionStorage with new user data
      try {
        const storedUserData = sessionStorage.getItem('userData');
        if (storedUserData) {
          const userData = JSON.parse(storedUserData);
          userData.account = accounts;
          sessionStorage.setItem('userData', JSON.stringify(userData));
          sessionStorage.setItem('userDataTimestamp', Date.now().toString());
        }
      } catch (storageError) {
        // Failed to update cache, but continue
      }
      
      return {
        wallets: wallets,
        accounts: accounts,
        userData: { account: accounts },
        notifications: data.NOTIFICATIONS || null,
        fullResponse: data
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update e-wallets amount (balance)
   * Similar to Flutter's updateEWalletsAmount()
   * @param {Array} walletsList - Current wallets list to update
   * @returns {Promise<Object>} Object containing updated wallets and notification counts
   * @throws {Error} If API call fails or response is invalid
   */
  static async updateEWalletsAmount(walletsList = []) {
    try {
      // Call the get-all-ewallets-amount API
      const response = await getAllEWalletsAmount();
      const data = response?.decryptedData || response?.data;
      
      // Validate response
      if (!data || data.CODE !== AppConstants.responseOk) {
        const errorMessage = data?.MESSAGE || `API Error (Code: ${data?.CODE || 'Unknown'})`;
        throw new Error(errorMessage);
      }
      
      // Update wallet amounts (similar to Flutter logic)
      const updatedWallets = [...walletsList];
      if (data.DATA && Array.isArray(data.DATA)) {
        data.DATA.forEach((element) => {
          try {
            const walletId = element.ID?.toString() || element.id?.toString();
            updatedWallets.forEach((wallet) => {
              const currentWalletId = wallet.ID?.toString() || wallet.id?.toString();
              if (walletId === currentWalletId) {
                // Update wallet balance, credit, and free margin
                if (element.CURRENT_BALENCE !== undefined) {
                  wallet.CURRENT_BALENCE = typeof element.CURRENT_BALENCE === 'number' 
                    ? element.CURRENT_BALENCE 
                    : parseFloat(element.CURRENT_BALENCE) || 0;
                }
                if (element.CURRENT_BALANCE !== undefined) {
                  wallet.CURRENT_BALANCE = typeof element.CURRENT_BALANCE === 'number' 
                    ? element.CURRENT_BALANCE 
                    : parseFloat(element.CURRENT_BALANCE) || 0;
                }
                if (element.CREDIT !== undefined) {
                  wallet.CREDIT = typeof element.CREDIT === 'number' 
                    ? element.CREDIT 
                    : parseFloat(element.CREDIT) || 0;
                }
                if (element.FREE_MARGIN !== undefined) {
                  wallet.FREE_MARGIN = typeof element.FREE_MARGIN === 'number' 
                    ? element.FREE_MARGIN 
                    : parseFloat(element.FREE_MARGIN) || 0;
                }
              }
            });
          } catch (e) {
            // Skip invalid wallet data
          }
        });
      }
      
      // Extract notification counts (similar to Flutter)
      let notificationCount = 0;
      let chatNotificationCount = 0;
      if (data.NOTIFICATIONS) {
        notificationCount = data.NOTIFICATIONS.NOTIFICATIONS ?? 0;
        chatNotificationCount = data.NOTIFICATIONS.CHAT_NOTIFICATIONS ?? 0;
      }
      
      return {
        wallets: updatedWallets,
        notificationCount: notificationCount,
        chatNotificationCount: chatNotificationCount,
        notifications: data.NOTIFICATIONS || null,
        fullResponse: data
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Patch cached session userData wallet balances without a full home-page reload.
   * @param {Array} walletRows - Wallet rows with ID and CURRENT_BALENCE fields
   */
  static patchCachedWalletBalances(walletRows) {
    if (!Array.isArray(walletRows) || walletRows.length === 0) return;

    const balanceById = new Map();
    for (const row of walletRows) {
      const id = row?.ID != null ? String(row.ID) : row?.id != null ? String(row.id) : null;
      if (!id) continue;
      balanceById.set(id, row);
    }
    if (balanceById.size === 0) return;

    try {
      const storedUserData = sessionStorage.getItem("userData");
      if (!storedUserData) return;

      const userData = JSON.parse(storedUserData);
      const accounts = userData?.account;
      if (!Array.isArray(accounts)) return;

      for (const account of accounts) {
        if (!account?.wallet || !Array.isArray(account.wallet)) continue;
        for (const wallet of account.wallet) {
          const id = wallet?.ID != null ? String(wallet.ID) : wallet?.id != null ? String(wallet.id) : null;
          const patch = id ? balanceById.get(id) : null;
          if (!patch) continue;

          if (patch.CURRENT_BALENCE !== undefined) {
            wallet.CURRENT_BALENCE = patch.CURRENT_BALENCE;
          }
          if (patch.CURRENT_BALANCE !== undefined) {
            wallet.CURRENT_BALANCE = patch.CURRENT_BALANCE;
          }
          if (patch.BALANCE !== undefined) {
            wallet.BALANCE = patch.BALANCE;
          }
        }
      }

      sessionStorage.setItem("userData", JSON.stringify(userData));
      sessionStorage.setItem("userDataTimestamp", Date.now().toString());
    } catch (_) {
      // Ignore cache patch errors
    }
  }

  /**
   * Refresh wallets in sessionStorage by reloading full wallet info and amounts.
   * This keeps wallet balances in sync across all screens that rely on cached userData.
   * @returns {Promise<Object|null>} Updated wallets result or null on failure
   */
  static async refreshWalletsCache() {
    try {
      let currentWallets = [];
      try {
        const walletData = await this.getWallets(true);
        if (Array.isArray(walletData?.wallets)) {
          currentWallets = walletData.wallets;
        }
      } catch (_) {
        // Fall through — still refresh amounts / notification counts from API.
      }

      const updatedResult = await this.updateEWalletsAmount(currentWallets);

      if (updatedResult && Array.isArray(updatedResult.wallets) && updatedResult.wallets.length > 0) {
        this.patchCachedWalletBalances(updatedResult.wallets);
      }

      try {
        sessionStorage.setItem("userDataTimestamp", Date.now().toString());
      } catch (_) {
        // Ignore cache timestamp errors
      }

      return updatedResult;
    } catch (_) {
      return null;
    }
  }
}

export default WalletController;
